import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Music, Volume2, RefreshCw, Radio, Repeat, List,
  Pause, Play, SkipForward, Square, ChevronDown, ChevronUp, X, BookOpen, Shuffle, GripVertical, Clock
} from 'lucide-react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL = 5000;

function EqBars({ active }) {
  if (!active) {
    return (
      <div className="flex items-end gap-0.5 h-5">
        {[3, 5, 3, 5, 3].map((h, i) => (
          <div key={i} className="w-1 bg-zinc-700 rounded-full" style={{ height: `${h * 2}px` }} />
        ))}
      </div>
    );
  }
  return (
    <div className="flex items-end gap-0.5 h-5">
      <div className="w-1 bg-[#5865F2] rounded-full eq-bar-1" style={{ height: '4px' }} />
      <div className="w-1 bg-[#D946EF] rounded-full eq-bar-2" style={{ height: '12px' }} />
      <div className="w-1 bg-[#5865F2] rounded-full eq-bar-3" style={{ height: '8px' }} />
      <div className="w-1 bg-[#D946EF] rounded-full eq-bar-4" style={{ height: '16px' }} />
      <div className="w-1 bg-[#5865F2] rounded-full eq-bar-1" style={{ height: '6px' }} />
    </div>
  );
}

function formatDuration(seconds) {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}:${String(m % 60).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function SortableQueueItem({ song, index, onRemove, removing }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: song._id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 group py-1.5 px-2 rounded-lg transition-colors ${isDragging ? 'bg-white/8' : 'hover:bg-[var(--bg-hover)]'}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="text-zinc-700 hover:text-zinc-400 cursor-grab active:cursor-grabbing flex-shrink-0 touch-none"
        title="Drag to reorder"
      >
        <GripVertical size={12} />
      </button>
      <span className="text-zinc-700 text-xs font-mono-stats w-4 flex-shrink-0">{index + 1}</span>
      <div className="flex-1 min-w-0">
        <p className="text-zinc-300 text-xs truncate font-[Manrope]">{song.title || 'Unknown'}</p>
      </div>
      <button
        onClick={() => onRemove(index)}
        disabled={removing === index}
        className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400 transition-all p-0.5 rounded flex-shrink-0"
        data-testid={`remove-queue-${index}`}
        title="Remove from queue"
      >
        {removing === index
          ? <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
          : <X size={12} />
        }
      </button>
    </div>
  );
}

function QueuePanel({ guildId, queueLength, onRemove }) {
  const [items, setItems] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [removing, setRemoving] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    if (!expanded) return;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/api/music/queue/${guildId}`);
        setItems((data.queue || []).map((s, i) => ({ ...s, _id: `${i}-${s.title}` })));
      } catch (e) { console.warn(e); }
    };
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [expanded, guildId]);

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id || !items) return;
    const from = items.findIndex(s => s._id === active.id);
    const to = items.findIndex(s => s._id === over.id);
    if (from < 0 || to < 0) return;
    setItems(arrayMove(items, from, to));
    try {
      await axios.post(`${API}/api/music/queue/${guildId}/reorder`, { from_index: from, to_index: to });
    } catch (e) {
      console.warn('Reorder failed:', e);
    }
  };

  const handleRemove = async (idx) => {
    setRemoving(idx);
    try {
      await axios.delete(`${API}/api/music/queue/${guildId}/${idx}`);
      setItems(prev => prev ? prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, _id: `${i}-${s.title}` })) : prev);
      if (onRemove) onRemove();
    } catch (e) {
      console.warn('Remove failed:', e);
    } finally {
      setRemoving(null);
    }
  };

  if (queueLength === 0 && !expanded) return null;

  return (
    <div className="border-t border-[var(--border)]">
      <button
        data-testid="queue-toggle-btn"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-4 py-2 text-zinc-500 hover:text-zinc-300 hover:bg-[var(--bg-hover)] transition-colors text-xs font-[Manrope]"
      >
        <div className="flex items-center gap-1.5">
          <List size={12} />
          <span>{queueLength} song{queueLength !== 1 ? 's' : ''} in queue</span>
        </div>
        <motion.div animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown size={13} />
        </motion.div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-3 max-h-64 overflow-y-auto" data-testid="queue-list">
              {!items ? (
                <p className="text-zinc-600 text-xs py-2 text-center">Loading queue...</p>
              ) : items.length === 0 ? (
                <p className="text-zinc-600 text-xs py-2 text-center">Queue is empty</p>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={items.map(s => s._id)} strategy={verticalListSortingStrategy}>
                    {items.map((song, i) => (
                      <SortableQueueItem
                        key={song._id}
                        song={song}
                        index={i}
                        onRemove={handleRemove}
                        removing={removing}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </div>
            <p className="text-zinc-700 text-[10px] text-center pb-2 font-[Manrope]">Drag to reorder</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ControlButton({ icon: Icon, label, onClick, variant = 'default', loading, testId }) {
  const styles = {
    default: 'bg-[var(--bg-hover)] hover:bg-white/10 text-zinc-300 hover:text-white border border-[var(--border)]',
    primary: 'bg-[#5865F2]/20 hover:bg-[#5865F2]/35 text-[#5865F2] border border-[#5865F2]/30',
    danger: 'bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 border border-red-500/20',
  };
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={loading}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium font-[Manrope] transition-all ${styles[variant]} disabled:opacity-50`}
    >
      {loading
        ? <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
        : <Icon size={13} />
      }
      {label}
    </button>
  );
}

function NowPlayingCard({ track, index, onControl }) {
  const [imgError, setImgError] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [localVolume, setLocalVolume] = useState(track.volume);
  const [twentyFourSeven, setTwentyFourSeven] = useState(null);
  const [toggling247, setToggling247] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/music/247/${track.guild_id}`)
      .then(r => setTwentyFourSeven(r.data.enabled))
      .catch(() => {});
  }, [track.guild_id]);

  const handle247 = async () => {
    setToggling247(true);
    try {
      const { data } = await axios.post(`${API}/api/music/247/${track.guild_id}`);
      setTwentyFourSeven(data.enabled);
    } catch (e) {
      console.warn('247 toggle failed:', e);
    } finally {
      setToggling247(false);
    }
  };

  const handleAction = async (action, extra = {}) => {
    setLoadingAction(action);
    setFeedback(null);
    try {
      const res = await onControl(track.guild_id, action, extra);
      if (res?.success === false) {
        setFeedback({ type: 'warn', msg: res.message });
      } else {
        setFeedback({ type: 'ok', msg:
          action === 'stop' ? 'Stopped' :
          action === 'skip' ? 'Skipped' :
          action === 'pause' ? 'Paused' :
          action === 'resume' ? 'Resumed' :
          action === 'shuffle' ? 'Queue shuffled' :
          action === 'loop' ? `Loop: ${res?.loop_mode || ''}` :
          `Volume ${localVolume}%` });
      }
    } catch (e) {
      setFeedback({ type: 'err', msg: 'Action failed. Try again.' });
    } finally {
      setLoadingAction(null);
      setTimeout(() => setFeedback(null), 2500);
    }
  };

  const handleVolume = async (val) => {
    setLocalVolume(val);
  };

  const commitVolume = async () => {
    await handleAction('volume', { value: localVolume });
  };

  const isPaused = track.is_paused;
  const isActive = track.is_playing && !isPaused;

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 6 }}
      transition={{ delay: index * 0.04 }}
      className="rounded-xl border border-[#5865F2]/20 bg-gradient-to-r from-[#5865F2]/8 to-transparent overflow-hidden"
      data-testid={`now-playing-card-${index}`}
    >
      {/* Main info row */}
      <div className="flex items-center gap-4 p-4">
        {/* Thumbnail with EQ overlay */}
        <div className="relative flex-shrink-0">
          {track.thumbnail && !imgError ? (
            <img
              src={track.thumbnail}
              alt={track.title}
              onError={() => setImgError(true)}
              className="w-14 h-14 rounded-xl object-cover"
            />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-[#5865F2]/20 flex items-center justify-center">
              <Music size={22} className="text-[#5865F2]" />
            </div>
          )}
          <div className="absolute -bottom-1.5 -right-1.5 bg-[var(--bg-surface)] border border-[var(--border-md)] rounded-md px-1 py-0.5">
            <EqBars active={isActive} />
          </div>
        </div>

        {/* Song info */}
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold font-[Outfit] text-sm truncate leading-tight">{track.title}</p>
          <p className="text-zinc-500 text-xs truncate font-[Manrope] mt-0.5">{track.uploader}</p>
          <div className="flex items-center gap-2.5 mt-1.5 flex-wrap">
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
              isPaused ? 'bg-amber-500/15 text-amber-400' : isActive ? 'bg-emerald-500/15 text-emerald-400' : 'bg-zinc-700/50 text-zinc-400'
            }`}>
              {isPaused ? 'Paused' : isActive ? 'Playing' : 'Idle'}
            </span>
            {track.duration > 0 && (
              <span className="text-zinc-600 text-xs font-mono-stats">{formatDuration(track.duration)}</span>
            )}
            {track.loop_mode !== 'off' && (
              <span className="flex items-center gap-1 text-[#5865F2] text-xs">
                <Repeat size={10} /> {track.loop_mode}
              </span>
            )}
          </div>
        </div>

        {/* Server + queue/vol info */}
        <div className="flex-shrink-0 hidden sm:flex flex-col items-end gap-1.5">
          <div className="flex items-center gap-1.5">
            {track.guild_icon ? (
              <img src={track.guild_icon} alt="" className="w-4 h-4 rounded-full" />
            ) : (
              <div className="w-4 h-4 rounded-full bg-[#5865F2]/30 flex items-center justify-center">
                <span className="text-[8px] text-[#5865F2] font-bold">#</span>
              </div>
            )}
            <span className="text-zinc-400 text-xs font-[Manrope] max-w-[100px] truncate">{track.guild_name}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-zinc-500">
              <List size={11} />
              <span className="text-xs font-mono-stats">{track.queue_length} in queue</span>
            </div>
          </div>
        </div>
      </div>

      {/* Controls row */}
      <div className="flex items-center gap-2 px-4 pb-4 pt-0 flex-wrap">
        {/* Pause / Resume */}
        <ControlButton
          icon={isPaused ? Play : Pause}
          label={isPaused ? 'Resume' : 'Pause'}
          variant="primary"
          loading={loadingAction === 'pause' || loadingAction === 'resume'}
          onClick={() => handleAction(isPaused ? 'resume' : 'pause')}
          testId={`btn-${isPaused ? 'resume' : 'pause'}-${index}`}
        />

        {/* Skip */}
        <ControlButton
          icon={SkipForward}
          label="Skip"
          variant="default"
          loading={loadingAction === 'skip'}
          onClick={() => handleAction('skip')}
          testId={`btn-skip-${index}`}
        />

        {/* Stop */}
        <ControlButton
          icon={Square}
          label="Stop"
          variant="danger"
          loading={loadingAction === 'stop'}
          onClick={() => handleAction('stop')}
          testId={`btn-stop-${index}`}
        />

        {/* Shuffle */}
        <ControlButton
          icon={Shuffle}
          label="Shuffle"
          variant="default"
          loading={loadingAction === 'shuffle'}
          onClick={() => handleAction('shuffle')}
          testId={`btn-shuffle-${index}`}
        />

        {/* Loop */}
        <ControlButton
          icon={Repeat}
          label={track.loop_mode === 'off' ? 'Loop: Off' : track.loop_mode === 'single' ? 'Loop: Track' : 'Loop: Queue'}
          variant={track.loop_mode !== 'off' ? 'primary' : 'default'}
          loading={loadingAction === 'loop'}
          onClick={() => handleAction('loop')}
          testId={`btn-loop-${index}`}
        />

        {/* 24/7 mode */}
        {twentyFourSeven !== null && (
          <ControlButton
            icon={Clock}
            label="24/7"
            variant={twentyFourSeven ? 'primary' : 'default'}
            loading={toggling247}
            onClick={handle247}
            testId={`btn-247-${index}`}
          />
        )}

        {/* Volume slider */}
        <div className="flex items-center gap-2 ml-auto">
          <Volume2 size={12} className="text-zinc-500 flex-shrink-0" />
          <input
            data-testid={`volume-slider-${index}`}
            type="range"
            min="1"
            max="100"
            value={localVolume}
            onChange={e => handleVolume(parseInt(e.target.value))}
            onMouseUp={commitVolume}
            onTouchEnd={commitVolume}
            className="w-20 h-1.5 rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #5865F2 ${localVolume}%, rgba(255,255,255,0.1) ${localVolume}%)`
            }}
          />
          <span className="text-zinc-500 text-xs font-mono-stats w-7">{localVolume}%</span>
        </div>

        {/* Feedback message */}
        <AnimatePresence>
          {feedback && (
            <motion.span
              initial={{ opacity: 0, x: 6 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              className={`text-xs font-[Manrope] ml-1 ${
                feedback.type === 'ok' ? 'text-emerald-400' :
                feedback.type === 'warn' ? 'text-amber-400' : 'text-red-400'
              }`}
            >
              {feedback.type === 'ok' ? '✓' : '!'} {feedback.msg}
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      <QueuePanel guildId={track.guild_id} queueLength={track.queue_length} onRemove={() => {}} />
    </motion.div>
  );
}

export default function NowPlayingWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const intervalRef = useRef(null);

  const fetchNowPlaying = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const { data: res } = await axios.get(`${API}/api/music/now-playing`);
      setData(res);
      setLastUpdated(new Date());
    } catch (e) {
      console.warn('Now playing fetch failed:', e.message);
    } finally {
      setLoading(false);
      if (showRefresh) setTimeout(() => setRefreshing(false), 400);
    }
  };

  useEffect(() => {
    fetchNowPlaying();
    intervalRef.current = setInterval(() => fetchNowPlaying(), POLL_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, []);

  const handleControl = async (guildId, action, extra = {}) => {
    try {
      const res = await axios.post(`${API}/api/music/control/${guildId}`, { action, ...extra });
      // Re-fetch after a short delay to reflect state change
      setTimeout(() => fetchNowPlaying(), 400);
      return res.data;
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      throw new Error(msg);
    }
  };

  const playing = data?.playing || [];
  const isAnythingPlaying = playing.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden mb-6"
      data-testid="now-playing-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--border)]">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isAnythingPlaying ? 'bg-emerald-400 pulse-online' : 'bg-zinc-600'}`} />
            <span className={`text-xs font-bold uppercase tracking-widest font-[Manrope] ${isAnythingPlaying ? 'text-emerald-400' : 'text-zinc-500'}`}>
              {isAnythingPlaying ? 'Live' : 'Idle'}
            </span>
          </div>
          <div className="w-px h-3.5 bg-white/10" />
          <div className="flex items-center gap-1.5">
            <Radio size={14} className="text-[#5865F2]" />
            <span className="text-sm font-semibold text-white font-[Outfit]">Now Playing</span>
          </div>
          {isAnythingPlaying && (
            <span className="text-xs text-zinc-500 font-[Manrope]">
              — {playing.length} server{playing.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-xs text-zinc-700 font-mono-stats hidden sm:block">
              {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
          <button
            data-testid="now-playing-refresh-btn"
            onClick={() => fetchNowPlaying(true)}
            className="p-1.5 rounded-lg hover:bg-[var(--bg-hover)] text-zinc-500 hover:text-zinc-300 transition-all"
            title="Refresh"
          >
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="flex items-center gap-3 py-3">
              <div className="w-14 h-14 bg-[var(--bg-hover)] rounded-xl animate-pulse flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3.5 bg-[var(--bg-hover)] rounded animate-pulse w-2/3" />
                <div className="h-3 bg-[var(--bg-hover)] rounded animate-pulse w-1/3" />
              </div>
            </motion.div>
          ) : !isAnythingPlaying ? (
            <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="flex items-center gap-4 py-3" data-testid="now-playing-empty">
              <div className="w-14 h-14 rounded-xl bg-[var(--bg-hover)] border border-[var(--border)] flex items-center justify-center flex-shrink-0">
                <div className="flex items-end gap-0.5 h-5">
                  {[2, 5, 3, 5, 2].map((h, i) => (
                    <div key={i} className="w-1 bg-zinc-700 rounded-full" style={{ height: `${h * 2}px` }} />
                  ))}
                </div>
              </div>
              <div>
                <p className="text-zinc-300 font-semibold font-[Outfit] text-sm mb-0.5">No music playing</p>
                <p className="text-zinc-600 text-xs font-[Manrope]">
                  Use <code className="text-[#5865F2] bg-[#5865F2]/10 px-1 rounded">/play</code> in Discord — controls will appear here live.
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div key="playing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="space-y-3">
              {playing.map((track, i) => (
                <NowPlayingCard key={track.guild_id} track={track} index={i} onControl={handleControl} />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="px-5 py-2 border-t border-[var(--border)] flex items-center justify-between">
        <span className="text-xs text-zinc-700 font-[Manrope]">Auto-refreshes every 5s</span>
        {isAnythingPlaying && (
          <motion.div
            animate={{ width: ['0%', '100%'] }}
            transition={{ duration: POLL_INTERVAL / 1000, ease: 'linear', repeat: Infinity }}
            className="h-0.5 bg-[#5865F2]/40 rounded-full max-w-[120px]"
          />
        )}
      </div>
    </motion.div>
  );
}
